import { LuArrowUpDown, LuChevronDown, LuChevronUp } from "react-icons/lu";
import { LuReply } from "react-icons/lu";

const COLUMNS = [
  { key: "id", name: "ID", sort_value: "id", className: "w-[7%]" },
  { key: "date", name: "Data", sort_value: "created_at", className: "w-[16%]" },
  { key: "title", name: "Titulli", sort_value: "title", className: "w-[28%]" },
  { key: "user", name: "Përdoruesi", sort_value: "first_name", className: "w-[18%]" },
  { key: "type", name: "Tipi", sort_value: "type", className: "w-[16%]" },
  { key: "status", name: "Statusi", className: "w-[15%]" },
];

const formatDateTime = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return d;
  }
};

const StatusPill = ({ replied }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold ring-1 ring-inset ${
      replied
        ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
        : "bg-amber-50 text-amber-700 ring-amber-200"
    }`}
  >
    <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60" />
    {replied ? "Përgjigjur" : "Në pritje"}
  </span>
);

const TypePill = ({ type }) => (
  <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-gray-100 text-gray-700 ring-1 ring-inset ring-gray-200">
    {type || "—"}
  </span>
);

const SkeletonRow = () => (
  <tr className="animate-pulse">
    <td className="px-6 py-4"><div className="h-3 w-8 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-3 w-24 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-3 w-48 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-3 w-32 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-5 w-20 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-5 w-20 bg-gray-200 rounded" /></td>
  </tr>
);

const SortIcon = ({ active, order }) => {
  if (!active)
    return (
      <LuArrowUpDown
        size={12}
        className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
      />
    );
  return order === "desc" ? (
    <LuChevronDown size={14} className="text-[var(--secondary-color)]" />
  ) : (
    <LuChevronUp size={14} className="text-[var(--secondary-color)]" />
  );
};

const truncate = (s, n = 60) => (!s ? "—" : s.length > n ? `${s.slice(0, n)}…` : s);

export default function ComplaintTable({
  complaints,
  isLoading,
  params = {},
  onSort,
  onRowClick,
  onReply,
}) {
  return (
    <div className="rounded-2xl border border-gray-200/80 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.03)] overflow-hidden">
      {/* Desktop */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              {COLUMNS.map((col) => {
                const sortable = !!col.sort_value;
                const active = sortable && params.sort_by === col.sort_value;
                return (
                  <th
                    key={col.key}
                    onClick={sortable ? () => onSort?.(col.sort_value) : undefined}
                    className={`group ${col.className} text-left px-6 py-3.5 text-[11px] font-semibold uppercase tracking-wider ${
                      active ? "text-[var(--secondary-color)]" : "text-gray-500"
                    } ${sortable ? "cursor-pointer select-none hover:text-gray-900" : ""} transition-colors`}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {col.name}
                      {sortable && <SortIcon active={active} order={params.sort_order} />}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (!complaints || complaints.length === 0)
              ? Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
              : complaints?.map((c) => (
                  <tr
                    key={c.complaint_id}
                    onClick={() => onRowClick?.(c)}
                    className="group cursor-pointer transition-colors hover:bg-gray-50/70"
                  >
                    <td className="px-6 py-3.5 text-gray-500 tabular-nums text-xs">
                      #{c.complaint_id}
                    </td>
                    <td className="px-6 py-3.5 text-gray-600 whitespace-nowrap tabular-nums text-xs">
                      {formatDateTime(c.created_at)}
                    </td>
                    <td className="px-6 py-3.5">
                      <span className="font-medium text-gray-900 truncate block max-w-[360px]">
                        {truncate(c.complaint_title, 60)}
                      </span>
                    </td>
                    <td className="px-6 py-3.5 text-gray-700 truncate">
                      {(c.first_name || c.last_name)
                        ? `${c.first_name || ""} ${c.last_name || ""}`.trim()
                        : "—"}
                    </td>
                    <td className="px-6 py-3.5">
                      <TypePill type={c.complaint_type} />
                    </td>
                    <td className="px-6 py-3.5">
                      {c.isReplied ? (
                        <StatusPill replied />
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onReply?.(c);
                          }}
                          className="inline-flex items-center gap-1.5 rounded-md bg-[var(--secondary-color)] hover:bg-[var(--secondary-color)]/90 px-2.5 py-1 text-xs font-semibold text-white shadow-sm transition"
                        >
                          <LuReply size={14} />
                          Përgjigju
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {/* Mobile */}
      <div className="md:hidden divide-y divide-gray-100">
        {isLoading && (!complaints || complaints.length === 0) ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="animate-pulse p-3 rounded-xl bg-gray-50 space-y-2">
                <div className="h-3 w-1/2 bg-gray-200 rounded" />
                <div className="h-2.5 w-3/4 bg-gray-100 rounded" />
              </div>
            ))}
          </div>
        ) : (
          complaints?.map((c) => (
            <button
              key={c.complaint_id}
              type="button"
              onClick={() => onRowClick?.(c)}
              className="w-full text-left p-4 hover:bg-gray-50 active:bg-gray-100 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 truncate">
                    {truncate(c.complaint_title, 80)}
                  </div>
                  <div className="text-xs text-gray-500 tabular-nums">#{c.complaint_id}</div>
                </div>
                <StatusPill replied={!!c.isReplied} />
              </div>
              <div className="mt-2 flex items-center gap-2">
                <TypePill type={c.complaint_type} />
                <span className="text-xs text-gray-500 truncate">
                  {(c.first_name || c.last_name)
                    ? `${c.first_name || ""} ${c.last_name || ""}`.trim()
                    : "—"}
                </span>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-xs text-gray-500 tabular-nums">
                  {formatDateTime(c.created_at)}
                </span>
                {!c.isReplied && (
                  <span
                    role="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onReply?.(c);
                    }}
                    className="inline-flex items-center gap-1.5 rounded-md bg-[var(--secondary-color)] hover:bg-[var(--secondary-color)]/90 px-2.5 py-1 text-xs font-semibold text-white shadow-sm transition"
                  >
                    <LuReply size={14} />
                    Përgjigju
                  </span>
                )}
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
